import { useNavigate } from "react-router-dom";

import chopLogo from "../../assets/chop_logo-2.svg";
import loginBg from "../../assets/login.webp";
import AuthLayout from "../layout/auth-layout";
import { useEffect, useState } from "react";
import useConfigService from "@/services/config.service";
import { useAgreementConsent } from "@/hooks/useAgreementConsent";
import parse from "html-react-parser";

interface TermsData {
  text: string;
  version: string;
  shouldDisplay: boolean;
}

const Agreement = () => {
  const navigate = useNavigate();
  const [termsData, setTermsData] = useState<TermsData>({
    text: "",
    version: "",
    shouldDisplay: true,
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { fetchTermsAndConditions, updateTermsAndConditionsAcceptance } =
    useConfigService();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { isAgreed, agree } = useAgreementConsent();

  const onAgree = async () => {
    try {
      setIsSubmitting(true);
      await updateTermsAndConditionsAcceptance(termsData.version);
      agree(termsData.version, true);
      navigate("/chat/chop-gpt", { replace: true });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to submit agreement"
      );
      console.error("Error submitting agreement:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Fetch terms and conditions from API
   */
  useEffect(() => {
    const loadTerms = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetchTermsAndConditions();

        if (response?.data) {
          setTermsData({
            text: response.data.terms_and_conditions,
            version: response.data.version,
            shouldDisplay: response.data.should_display,
          });

          if (!response.data.should_display) {
            if (!isAgreed) {
              agree(response.data.version, true);
            }
            navigate("/chat/chop-gpt", { replace: true });
            return;
          } else {
            if (isAgreed) {
              agree(response.data.version, false);
            }
          }
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load terms and conditions"
        );
        console.error("Error fetching terms and conditions:", err);
      } finally {
        setIsLoading(false);
      }
    };

    loadTerms();
  }, []);

  return (
    <AuthLayout backgroundUrl={loginBg}>
      <div className="h-full w-full flex flex-col justify-center items-center">
        <img
          src={chopLogo}
          alt="Children's Hospital of Philadelphia"
          className="mx-auto h-16 w-auto mb-4 md:mb-6"
        />
        <div className="bg-white/95 shadow-xl rounded-lg w-full text-left max-w-4xl font-normal">
          <div className="mx-6 mt-6">
            <div className="text-[15px] md:text-lg font-semibold font-primary text-primary-500 mb-2">
              Terms and Conditions
            </div>
            <p className="text-[#09090B] text-sm md:text-[15px] mb-2 font-secondary font-normal">
              Kindly review the terms and conditions thoroughly before
              proceeding
            </p>
            <div className="border-gray-200 rounded-md md:pr-4 bg-white max-h-[50svh] md:max-h-[48svh] overflow-y-auto">
              {isLoading ? (
                <div className="flex justify-center items-center h-40">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                </div>
              ) : error ? (
                <div className="text-red-500 text-sm text-center p-4">
                  {error}
                </div>
              ) : (
                <div
                  className="text-[#71717B] text-xs md:text-sm leading-relaxed font-secondary text-justify 
                  [&_ul]:list-disc [&_ul]:pl-6 [&_a]:text-primary-500 [&_a]:underline hover:[&_a]:text-primary-600  [&_h3]:font-semibold [&_h3]:my-2"
                >
                  {parse(termsData.text)}
                </div>
              )}
            </div>
          </div>
          <div className="p-6 flex justify-end sticky bottom-[0px] bg-[#fefefe]">
            <button
              onClick={onAgree}
              disabled={isLoading || isSubmitting}
              className="px-6 py-2 rounded-md bg-primary-500 text-white hover:bg-primary-600 transition-colors shadow-sm w-full md:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Processing..." : "Agree"}
            </button>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default Agreement;
